import { useEffect, useRef } from 'react';
import { useWaitingCallStore } from '@modules/phone/store/waiting-call.store';

const BEEP_INTERVAL_MS = 3000;
const BEEP_FREQUENCY_HZ = 880;
const BEEP_PULSES = 2;
const BEEP_PULSE_DURATION_S = 0.12;
const BEEP_PULSE_GAP_S = 0.18;
// deliberately quiet — the beep overlaps a live call and must not talk over it
const BEEP_GAIN = 0.1;

/**@description Recurring notification beep for a second (waiting) call. Twilio's own incoming
 * ringtone is unreliable while the output device is already carrying an active call, so the
 * waiting call gets its own synthesised tone — same oscillator approach as `useTouchTone`,
 * which keeps this asset-free. Stops as soon as the call is silenced, accepted, declined or
 * cancelled, since every one of those paths clears `waitingCall` or sets `isSilenced`. */
export function useWaitingCallBeep() {
  const waitingCall = useWaitingCallStore((state) => state.waitingCall);
  const isSilenced = useWaitingCallStore((state) => state.isSilenced);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(
    function beepWhileWaitingCallRings() {
      if (!waitingCall || isSilenced) return;

      function playBeep() {
        try {
          // one context reused for every beep — browsers cap how many a page may create
          audioContextRef.current ??= new AudioContext();
          const audioContext = audioContextRef.current;

          // a tab that has not had a user gesture yet starts its context suspended
          if (audioContext.state === 'suspended') void audioContext.resume();

          for (let pulse = 0; pulse < BEEP_PULSES; pulse++) {
            const startAt = audioContext.currentTime + pulse * BEEP_PULSE_GAP_S;
            const oscillator = audioContext.createOscillator();
            const gain = audioContext.createGain();

            oscillator.connect(gain);
            gain.connect(audioContext.destination);
            oscillator.frequency.value = BEEP_FREQUENCY_HZ;

            gain.gain.setValueAtTime(BEEP_GAIN, startAt);
            gain.gain.setValueAtTime(0, startAt + BEEP_PULSE_DURATION_S);
            oscillator.start(startAt);
            oscillator.stop(startAt + BEEP_PULSE_DURATION_S);
          }
        } catch {
          console.error('Error playing waiting call beep');
        }
      }

      playBeep();
      const intervalId = setInterval(playBeep, BEEP_INTERVAL_MS);

      return () => clearInterval(intervalId);
    },
    [waitingCall, isSilenced]
  );

  useEffect(function closeAudioContextOnUnmount() {
    return () => {
      void audioContextRef.current?.close();
      audioContextRef.current = null;
    };
  }, []);
}
